import { LoadingState } from '@lcs/shared/api';
import { createFeatureSelector, on } from '@ngrx/store';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { RegisterState } from 'src/app/common-components/register/models/register-state';
import {
   RegisterAdapter,
   createRegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';
import { CallLogsFilterOptions } from '../models/call-logs-filter-option';
import { CallLogsRegisterOptions } from '../models/call-logs-register-options';
import { CallLogsRegisterListItem } from '../models/call-logs-register-results.model';
import * as callLogsRegisterActions from './call-logs-register.actions';
import { callLogsRegisterAdapterActions } from './call-logs-register.actions';

export const callLogsRegisterFeatureKey = 'callLogsRegister';
export interface CallLogsRegisterState
   extends RegisterState<CallLogsRegisterListItem, CallLogsRegisterOptions, OrderingOption> {
   filters: CallLogsFilterOptions[];
}

const adapter: RegisterAdapter<CallLogsRegisterListItem, CallLogsRegisterOptions, OrderingOption> =
   createRegisterAdapter<CallLogsRegisterListItem, CallLogsRegisterOptions, OrderingOption>({
      selectId: (callLogsRegisterItem) => {
         return callLogsRegisterItem.callDetailId ?? 0;
      },
   });

const today = new Date();
const thirtyDaysAgo = new Date();
thirtyDaysAgo.setDate(today.getDate() - 30);
const initialQuickFilters: CallLogsRegisterOptions = {
   startDate: thirtyDaysAgo,
   endDate: today,
   fromDuration: undefined,
   toDuration: undefined,
   callDirection: undefined,
   callDisposition: undefined,
   extensionGroupExtensions: undefined,
   searchText: '',
};

const initialState: CallLogsRegisterState = {
   ...adapter.getInitialState(),
   filters: [],
   orderingOptions: [{ direction: OrderingDirections.Desc, field: 'startTime' }],
   quickFilters: initialQuickFilters,
};

export const callLogsRegisterReducer = adapter.createReducer(
   callLogsRegisterActions.callLogsRegisterAdapterActions,
   initialState,
   on(callLogsRegisterActions.callLogsRegisterAdapterActions.pageEnter, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(callLogsRegisterActions.callLogsRegisterAdapterActions.loadSuccess, (state, action) => {
      return {
         ...state,
         callLogs: action.items,
         callState: LoadingState.Loaded,
         endCursor: action.endCursor,
         hasNextPage: action.hasNextPage,
         totalCount: action.totalCount,
      };
   }),
   on(callLogsRegisterActions.callLogsRegisterInitialFilter, (state, action) => {
      const hasQuickFilters = action.quickFilters && Object.keys(action.quickFilters).length > 0;
      return {
         ...state,
         callState: LoadingState.Loading,
         filters: action.filters,
         callLogs: [],
         endCursor: null,
         quickFilters: hasQuickFilters ? action.quickFilters : initialQuickFilters,
      };
   }),
   on(callLogsRegisterAdapterActions.quickFilterChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         quickFilters: {
            ...state.quickFilters,
            ...action.quickFilters,
         },
      };
   }),
   on(callLogsRegisterActions.callLogsRegisterAdapterActions.orderingOptionsChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         orderingOptions: state.orderingOptions
            ? [
                 ...state.orderingOptions
                    .filter((option) => !action.orderingOptions.some((newOption) => newOption.field === option.field))
                    .filter((option) => option.direction !== OrderingDirections.NotSet),
                 ...action.orderingOptions,
              ]
            : action.orderingOptions,
      };
   })
);
export const callLogsRegisterSelector = createFeatureSelector<CallLogsRegisterState>(callLogsRegisterFeatureKey);
export const selectors = adapter.getSelectors(callLogsRegisterSelector);
export const {
   selectAll,
   selectCallState,
   selectTotalCount,
   selectEndCursor,
   selectQuickFilters,
   selectOrderingOptions,
} = adapter.getSelectors(callLogsRegisterSelector);
